import React from 'react'
import styled from 'styled-components'

/* Shared search/filter bar for the order tabs: matches order-ID fragments,
   phone numbers (digits compared, so formatting doesn't matter), and a
   created-at date range. Filtering is client-side over the already-fetched
   order list, applied before pagination. */

const digits = s => (s || '').replace(/\D/g, '')

export const emptyFilters = { query: '', from: '', to: '' }

export const hasActiveFilters = f => !!(f.query.trim() || f.from || f.to)

export const filterOrders = (orders, f) => {
  const q = f.query.trim().toLowerCase()
  const qDigits = digits(q)
  const fromT = f.from ? new Date(f.from + 'T00:00:00').getTime() : null
  const toT = f.to ? new Date(f.to + 'T23:59:59.999').getTime() : null

  return (orders || []).filter(o => {
    if (q) {
      const idMatch = o.id && o.id.toLowerCase().includes(q)
      const phone = o.shippingAddress && o.shippingAddress.phone_number
      const phoneMatch =
        (phone && phone.toLowerCase().includes(q)) ||
        (qDigits.length >= 3 && digits(phone).includes(qDigits))
      if (!idMatch && !phoneMatch) return false
    }
    if (fromT || toT) {
      const t = new Date(o.createdAt).getTime()
      if (fromT && t < fromT) return false
      if (toT && t > toT) return false
    }
    return true
  })
}

const OrderSearch = ({ filters, onChange, total, found }) => {
  const set = patch => onChange({ ...filters, ...patch })
  const active = hasActiveFilters(filters)

  return (
    <Bar>
      <QueryInput
        type="text"
        value={filters.query}
        placeholder="Search order ID or phone…"
        aria-label="Search orders by ID or phone"
        onChange={e => set({ query: e.target.value })}
      />

      <DateField>
        <span>From</span>
        <input
          type="date"
          value={filters.from}
          aria-label="Orders from date"
          onChange={e => set({ from: e.target.value })}
        />
      </DateField>

      <DateField>
        <span>To</span>
        <input
          type="date"
          value={filters.to}
          aria-label="Orders to date"
          onChange={e => set({ to: e.target.value })}
        />
      </DateField>

      {active && (
        <>
          <Count>
            {found} of {total}
          </Count>
          <ClearBtn type="button" onClick={() => onChange({ ...emptyFilters })}>
            Clear
          </ClearBtn>
        </>
      )}
    </Bar>
  )
}

export default OrderSearch

/* ---------- styles ---------- */
const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 16px;
`;

const QueryInput = styled.input`
  flex: 1 1 220px;
  min-width: 0;
  padding: 10px 14px;
  font-size: 0.95rem;
  color: #1f2d3d;
  background: #fff;
  border: 1px solid #dfe3e8;
  border-radius: 10px;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #aab4bf;
  }

  &:focus {
    outline: none;
    border-color: #01bf71;
    box-shadow: 0 0 0 3px rgba(1, 191, 113, 0.15);
  }
`;

const DateField = styled.label`
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #8a99a8;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  input {
    padding: 8px 10px;
    font-size: 0.9rem;
    color: #1f2d3d;
    border: 1px solid #dfe3e8;
    border-radius: 10px;
    background: #fff;

    &:focus {
      outline: none;
      border-color: #01bf71;
      box-shadow: 0 0 0 3px rgba(1, 191, 113, 0.15);
    }
  }

  @media screen and (max-width: 480px) {
    flex: 1 1 45%;

    input {
      width: 100%;
    }
  }
`;

const Count = styled.span`
  flex: 0 0 auto;
  font-size: 0.82rem;
  font-weight: 700;
  color: #0a8f57;
  background: rgba(1, 191, 113, 0.1);
  padding: 4px 10px;
  border-radius: 999px;
`;

const ClearBtn = styled.button`
  flex: 0 0 auto;
  border: 1px solid rgba(31, 45, 61, 0.15);
  background: transparent;
  color: #6a7b8a;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 7px 16px;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: #d32f2f;
    border-color: rgba(239, 83, 80, 0.4);
  }
`;
